import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { convertToGeoLocation } from "@src/appV2/Location";
import { HorizontalScrollView } from "@src/appV2/redesign/system/ui-components";
import {
  type ExtraWorkedTimeRequestEntity,
  type ExtraWorkedTimeRequestShift,
  ExtraWorkedTimeRequestStatus as ExtraWorkedTimeRequestStatusEnum,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { compareDesc, differenceInDays, parseISO, startOfDay, subDays } from "date-fns";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useGetExtraWorkedTimeRequests } from "../../api/useGetExtraWorkedTimeRequests";
import { usePatchExtraWorkedTimeRequestMetadata } from "../../api/usePatchExtraWorkedTimeRequestMetadata";
import { useWorkerUnverifiedShifts } from "../../UnverifiedShifts/useWorkerUnverifiedShifts";
import { type ShiftActionParams } from "../Page";
import {
  buildExtraWorkedTimeRequestCard,
  buildUnverifiedShiftCard,
  type ExtraWorkedTimeRequestInput,
} from "./getShiftHistoryBannerProps";
import { ShiftHistoryBannerCard, type ShiftHistoryBannerCardData } from "./ShiftHistoryBannerCard";

const TIMESHEET_SUBMISSION_WINDOW_IN_DAYS = 30;
const RESOLVED_REQUESTS_MAX_AGE_DAYS = 30;

interface ShiftHistoryBannerListProps {
  onUploadTimesheetClick: (params: ShiftActionParams) => void;
  onRequestShiftAdjustmentClick: (params: ShiftActionParams) => Promise<void>;
  isCheckingGeofence: boolean;
  selectedShift: ShiftActionParams | undefined;
}

export function ShiftHistoryBannerList(props: ShiftHistoryBannerListProps) {
  const {
    onUploadTimesheetClick,
    onRequestShiftAdjustmentClick,
    isCheckingGeofence,
    selectedShift,
  } = props;

  const history = useHistory();

  const updatedAtFilter = useMemo(
    () => subDays(startOfDay(new Date()), RESOLVED_REQUESTS_MAX_AGE_DAYS),
    []
  );

  const { data: extraWorkedTimeRequestsData } = useGetExtraWorkedTimeRequests({
    updatedAtFilter,
  });
  const { mutate: patchExtraWorkedTimeRequestMetadata } = usePatchExtraWorkedTimeRequestMetadata();

  const extraWorkedTimeRequests = useMemo((): ExtraWorkedTimeRequestInput[] => {
    if (!extraWorkedTimeRequestsData?.data) {
      return [];
    }

    const shiftMap = new Map<string, ExtraWorkedTimeRequestShift>(
      extraWorkedTimeRequestsData.included
        .filter((item): item is ExtraWorkedTimeRequestShift => item.type === "shift")
        .map((shift) => [shift.id, shift])
    );

    const deduplicatedRequests = [
      ...extraWorkedTimeRequestsData.data
        .toSorted((a, b) =>
          compareDesc(parseISO(a.attributes.updatedAt), parseISO(b.attributes.updatedAt))
        )
        .reduce((map, request) => {
          if (!map.has(request.attributes.shiftId)) {
            map.set(request.attributes.shiftId, request);
          }
          return map;
        }, new Map<string, ExtraWorkedTimeRequestEntity>())
        .values(),
    ];

    return deduplicatedRequests
      .filter(
        ({ attributes: { status }, meta }) =>
          status !== ExtraWorkedTimeRequestStatusEnum.PENDING && !meta?.isNotificationDismissed
      )
      .map(
        ({
          attributes: {
            shiftId,
            status,
            isFullShiftPayment,
            updatedAt,
            statusUpdatedBy,
            statusUpdateReason,
          },
          id,
        }) => {
          const shift = shiftMap.get(shiftId);
          if (!shift) {
            return undefined;
          }

          const { facility, start, verified } = shift.attributes;

          return {
            id,
            status: status as ExtraWorkedTimeRequestInput["status"],
            workplaceName: facility.name,
            workplaceTimezone: facility.tmz,
            shiftDate: parseISO(start),
            shiftId,
            updatedAt,
            isVerified: verified,
            isFullShiftPayment,
            statusUpdatedBy,
            statusUpdateReason,
          };
        }
      )
      .filter(isDefined);
  }, [extraWorkedTimeRequestsData]);

  const { data: unverifiedShiftsData } = useWorkerUnverifiedShifts();

  const bannerCards = useMemo((): ShiftHistoryBannerCardData[] => {
    const ewtrCards = extraWorkedTimeRequests.map((request) => {
      return buildExtraWorkedTimeRequestCard({
        request,
        onDismiss: () => {
          patchExtraWorkedTimeRequestMetadata({
            extraWorkedTimeRequestId: request.id,
            metadata: { isNotificationDismissed: true },
          });
        },
        onNavigate: (path) => history.push(path),
      });
    });

    const filteredUnverifiedShifts = (unverifiedShiftsData?.response ?? []).filter(
      (shift) =>
        (shift.timecard?.files?.length ?? 0) === 0 &&
        !shift.hasPendingFullShiftExtraWorkedTimeRequest &&
        differenceInDays(new Date(), parseISO(shift.start)) <= TIMESHEET_SUBMISSION_WINDOW_IN_DAYS
    );

    const unverifiedCards = filteredUnverifiedShifts.map((shift) => {
      const shiftId = shift._id;
      const workplaceId = shift.facility.userId ?? shift.facilityId;
      const isMissedPunchRequestEnabled =
        shift.facility.featureSettings?.isMissedPunchRequestEnabled ?? false;
      const workplaceLocation = isDefined(shift.facility.geoLocation?.coordinates)
        ? convertToGeoLocation(shift.facility.geoLocation.coordinates)
        : undefined;

      const actionParams: ShiftActionParams = {
        shiftId,
        workplaceId,
        workplaceLocation,
        shiftEndAt: shift.end,
        isVerified: false,
        isTimesheetUploaded: false,
      };

      return buildUnverifiedShiftCard({
        shiftId,
        workplaceName: shift.facility.name,
        workplaceTimezone: shift.facility.tmz,
        shiftDate: parseISO(shift.start),
        shiftEndAt: shift.end,
        ctaButtonText: isMissedPunchRequestEnabled ? "Submit timesheet" : "Upload timesheet",
        isLoading: selectedShift?.shiftId === shiftId && isCheckingGeofence,
        onCtaClick: () => {
          if (isMissedPunchRequestEnabled) {
            void onRequestShiftAdjustmentClick(actionParams);
          } else {
            onUploadTimesheetClick(actionParams);
          }
        },
      });
    });

    const allCards = [...ewtrCards, ...unverifiedCards];
    allCards.sort((a, b) => compareDesc(a.sortDate, b.sortDate));
    return allCards;
  }, [
    extraWorkedTimeRequests,
    unverifiedShiftsData,
    patchExtraWorkedTimeRequestMetadata,
    history,
    selectedShift,
    isCheckingGeofence,
    onRequestShiftAdjustmentClick,
    onUploadTimesheetClick,
  ]);

  if (bannerCards.length === 0) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 6, marginX: -5 }}>
      <HorizontalScrollView stretch sx={{ px: 5 }}>
        {bannerCards.map((card) => (
          <ShiftHistoryBannerCard key={card.id} data={card} />
        ))}
      </HorizontalScrollView>
    </Box>
  );
}
